import {
  Inject,
  Injectable,
  Optional,
  PLATFORM_ID,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@environments/environment';
import {
  PixelConfiguration,
  SnapPixelEventName,
  SnapPixelEventProperties,
} from '@share/pixel-code/pixel-model/pixel.models';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs/operators';

declare let snaptr: Function;

@Injectable({
  providedIn: 'root',
})
export class SnapPixelService {
  private doc: Document;
  private renderer: Renderer2;
  private config: PixelConfiguration = environment.snap_pixel_config;

  constructor(
    @Inject(DOCUMENT) private injectedDocument: any,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() private router: Router,
    private rendererFactory: RendererFactory2
  ) {
    // DOCUMENT cannot be injected directly as Document type, see https://github.com/angular/angular/issues/20351
    // It is therefore injected as any and then cast to Document
    this.doc = injectedDocument as Document;
    this.renderer = rendererFactory.createRenderer(null, null);

    if (router) {
      // Log page views after router navigation ends
      router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          if (this.isLoaded()) {
            this.track('PAGE_VIEW');
          }
        });
    }
  }

  /**
   * Initialize the Pixel tracking script
   * - Adds the script to page's head
   * - Tracks first page view
   */
  initialize(pixelId = this.config.pixelId): void {
    if (!this.config) {
      throw Error(
        'snap--pixel not configured correctly. Pass the `pixelId` property to the env file'
      );
    }
    if (!this.config.enabled) {
      return;
    }
    if (this.isLoaded()) {
      console.warn(
        'Tried to initialize a Snap Pixel instance while another is already active. Please call `remove()` before initializing a new instance.'
      );
      return;
    }
    this.config.enabled = true;
    this.addPixelScript(pixelId);
  }

  /** Remove the Pixel tracking script */
  remove(): void {
    this.removePixelScript();
    this.config.enabled = false;
  }

  /**
   * Track a Standard Event as predefined by Snap
   *
   * See {@link https://businesshelp.snapchat.com/s/article/pixel-website-install?language=en_US Snap Pixel docs - reference}
   * @param eventName The name of the event that is being tracked
   * @param properties Optional properties of the event
   */
  track(eventName: SnapPixelEventName, properties?: SnapPixelEventProperties): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.isLoaded()) {
      console.warn(
        'Tried to track an event without initializing a Snap Pixel instance. Call `initialize()` first.'
      );
      return;
    }

    if (properties) {
      snaptr('track', eventName, properties);
    } else {
      snaptr('track', eventName);
    }
  }
  /**
   * Adds the Snap Pixel tracking script to the application
   * @param pixelId The Snap Pixel ID to use
   */
  private addPixelScript(pixelId: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const pixelCode = `
    var pixelCode = function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
      {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
        a.queue=[];var s='script';r=t.createElement(s);r.async=!0;
        r.src=n;var u=t.getElementsByTagName(s)[0];
        u.parentNode.insertBefore(r,u);}(window,document,
        'https://sc-static.net/scevent.min.js');
      snaptr('init', '${pixelId}');
      snaptr('track', 'PAGE_VIEW');`

    const scriptElement = this.renderer.createElement('script');
    this.renderer.setAttribute(scriptElement, 'id', 'snap-pixel-script');
    this.renderer.setAttribute(scriptElement, 'type', 'text/javascript');
    this.renderer.setProperty(scriptElement, 'innerHTML', pixelCode);
    this.renderer.appendChild(this.doc.head, scriptElement);
  }

  /** Remove Snap Pixel tracking script from the application */
  private removePixelScript(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const pixelElement = this.doc.getElementById('pixel-script');
    if (pixelElement) {
      pixelElement.remove();
    }
  }

  /** Checks if the script element is present */
  private isLoaded(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const pixelElement = this.doc.getElementById('snap-pixel-script');
      return !!pixelElement;
    }
    return false;
  }
}
